import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { getStoreCapabilities } from '@/lib/accountType';
import { getProductImage } from '@/lib/productMedia';
import { useHomeConfig } from '@/hooks/useHomeConfig';
import {
  HOME_BLOCKS,
  defaultBlockConfig,
  blockLabel,
  SERVICE_COLUMN_OPTIONS,
  PRODUCT_COLUMN_OPTIONS,
  type HomeBlockConfig,
} from '@/lib/homeBlocks';
import SortableBlockRow from './components/SortableBlockRow';
import FeaturedList, { type FeaturedItem } from './components/FeaturedList';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';

interface FeaturedProductRow {
  id: number;
  name: string;
  media: unknown;
  is_featured: boolean | null;
}

interface FeaturedServiceRow {
  id: number;
  titre: string;
  product_image: string | null;
  is_featured: boolean | null;
}

export default function HomeBlocksPage() {
  const { user } = useAuth();
  const merchantId = user ? getCommerceId(user) : null;
  const { config, loading, error, saveBlocks, setServicesColumns, setProductsColumns, saveAbout, saveCta } = useHomeConfig(merchantId);
  const [blocks, setBlocks] = useState<HomeBlockConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Services de réservation (pour le calendrier par défaut)
  const [bookingServices, setBookingServices] = useState<{ id: number; service_name: string }[]>([]);
  const [defaultServiceId, setDefaultServiceId] = useState<number | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [defaultSaved, setDefaultSaved] = useState(false);

  // Mise en page de la grille « Nos services »
  const [serviceColumns, setServiceColumns] = useState<number>(3);
  const [savingColumns, setSavingColumns] = useState(false);
  const [columnsSaved, setColumnsSaved] = useState(false);

  // Mise en page de la grille « Nos produits »
  const [productColumns, setProductColumns] = useState<number>(3);
  const [savingProductColumns, setSavingProductColumns] = useState(false);
  const [productColumnsSaved, setProductColumnsSaved] = useState(false);

  // Bloc « À propos / présentation »
  const [aboutTitle, setAboutTitle] = useState('');
  const [aboutContent, setAboutContent] = useState('');
  const [savingAbout, setSavingAbout] = useState(false);
  const [aboutSaved, setAboutSaved] = useState(false);

  // Bloc « Appel à l'action »
  const [ctaTitle, setCtaTitle] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaButtonLabel, setCtaButtonLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [savingCta, setSavingCta] = useState(false);
  const [ctaSaved, setCtaSaved] = useState(false);

  // Éléments à marquer « vedette » (produits / services)
  const [featuredProducts, setFeaturedProducts] = useState<FeaturedProductRow[]>([]);
  const [featuredServices, setFeaturedServices] = useState<FeaturedServiceRow[]>([]);

  useEffect(() => {
    setBlocks(config.blocks);
    setDefaultServiceId(config.defaultBookingServiceId);
    setServiceColumns(config.servicesColumns);
    setProductColumns(config.productsColumns);
    setAboutTitle(config.aboutTitle);
    setAboutContent(config.aboutContent);
    setCtaTitle(config.ctaTitle);
    setCtaText(config.ctaText);
    setCtaButtonLabel(config.ctaButtonLabel);
    setCtaUrl(config.ctaUrl);
  }, [config]);

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [prodRes, svcRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('id, name, media, is_featured')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(60),
          supabase
            .from('nospartenairesservices')
            .select('id, titre, product_image, is_featured')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(60),
        ]);
        if (cancelled) return;
        setFeaturedProducts((prodRes.data as FeaturedProductRow[]) || []);
        setFeaturedServices((svcRes.data as FeaturedServiceRow[]) || []);
      } catch {
        if (!cancelled) {
          setFeaturedProducts([]);
          setFeaturedServices([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('booking_services')
      .select('id, service_name')
      .eq('owner', user.id)
      .eq('statut', 1)
      .order('id')
      .then(({ data }) => setBookingServices((data as { id: number; service_name: string }[]) || []))
      .catch(() => setBookingServices([]));
  }, [user]);

  const caps = getStoreCapabilities(user?.type, user?.typecompte);
  // Blocs visibles : ceux transverses (bannière) + ceux pertinents pour le type de compte.
  // Un compte Mixte voit donc les blocs produits ET services.
  const visibleDefs = HOME_BLOCKS.filter((b) => {
    if (b.scope === 'both') return true;
    // « Produits en vedette » est inutile pour la restauration (remplacé par la Promo du moment).
    if (b.key === 'featured_products' && caps.restaurant) return false;
    if (b.scope === 'products') return caps.products;
    if (b.scope === 'services') return caps.services;
    if (b.scope === 'restaurant') return caps.restaurant;
    return false;
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const visibleKeys = visibleDefs.map((d) => d.key);
      const oldIndex = visibleKeys.indexOf(String(active.id));
      const newIndex = visibleKeys.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(visibleKeys, oldIndex, newIndex);
      // Réinjecte le nouvel ordre aux positions occupées par les blocs visibles.
      setBlocks((prev) => {
        const next = [...prev];
        const positions: number[] = [];
        next.forEach((b, i) => {
          if (visibleKeys.includes(b.key)) positions.push(i);
        });
        positions.forEach((pos, i) => {
          const key = reordered[i];
          const found = next.find((b) => b.key === key);
          if (found) next[pos] = found;
        });
        return next;
      });
    },
    [visibleDefs],
  );

  const toggleBlock = (key: string, enabled: boolean) => {
    setBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, enabled } : b)));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    const ok = await saveBlocks(blocks);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const handleReset = () => {
    setBlocks(defaultBlockConfig());
  };

  const handleSaveDefault = async () => {
    if (!merchantId) return;
    setSavingDefault(true);
    setDefaultSaved(false);
    try {
      const { error: upsertError } = await supabase
        .from('merchant_home_config')
        .upsert(
          {
            idcommerce: merchantId,
            default_booking_service_id: defaultServiceId,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'idcommerce' },
        );
      if (upsertError) throw upsertError;
      setDefaultSaved(true);
      setTimeout(() => setDefaultSaved(false), 2500);
    } catch {
      // silent
    } finally {
      setSavingDefault(false);
    }
  };

  const handleSaveColumns = async () => {
    setSavingColumns(true);
    setColumnsSaved(false);
    const ok = await setServicesColumns(serviceColumns);
    setSavingColumns(false);
    if (ok) {
      setColumnsSaved(true);
      setTimeout(() => setColumnsSaved(false), 2500);
    }
  };

  const handleSaveProductColumns = async () => {
    setSavingProductColumns(true);
    setProductColumnsSaved(false);
    const ok = await setProductsColumns(productColumns);
    setSavingProductColumns(false);
    if (ok) {
      setProductColumnsSaved(true);
      setTimeout(() => setProductColumnsSaved(false), 2500);
    }
  };

  const handleSaveAbout = async () => {
    setSavingAbout(true);
    setAboutSaved(false);
    const ok = await saveAbout(aboutTitle.trim(), aboutContent.trim());
    setSavingAbout(false);
    if (ok) {
      setAboutSaved(true);
      setTimeout(() => setAboutSaved(false), 2500);
    }
  };

  const handleSaveCta = async () => {
    setSavingCta(true);
    setCtaSaved(false);
    const ok = await saveCta({
      title: ctaTitle.trim(),
      text: ctaText.trim(),
      buttonLabel: ctaButtonLabel.trim(),
      url: ctaUrl.trim(),
    });
    setSavingCta(false);
    if (ok) {
      setCtaSaved(true);
      setTimeout(() => setCtaSaved(false), 2500);
    }
  };

  const handleToggleProductFeatured = async (id: number, next: boolean) => {
    setFeaturedProducts((prev) => prev.map((p) => (p.id === id ? { ...p, is_featured: next } : p)));
    try {
      const { error: updateError } = await supabase.from('product_items').update({ is_featured: next }).eq('id', id);
      if (updateError) throw updateError;
    } catch {
      setFeaturedProducts((prev) => prev.map((p) => (p.id === id ? { ...p, is_featured: !next } : p)));
    }
  };

  const handleToggleServiceFeatured = async (id: number, next: boolean) => {
    setFeaturedServices((prev) => prev.map((s) => (s.id === id ? { ...s, is_featured: next } : s)));
    try {
      const { error: updateError } = await supabase.from('nospartenairesservices').update({ is_featured: next }).eq('id', id);
      if (updateError) throw updateError;
    } catch {
      setFeaturedServices((prev) => prev.map((s) => (s.id === id ? { ...s, is_featured: !next } : s)));
    }
  };

  const productItems: FeaturedItem[] = featuredProducts.map((p) => ({
    id: p.id,
    label: p.name,
    image: getProductImage(p.media) || null,
    featured: Boolean(p.is_featured),
  }));

  const serviceItems: FeaturedItem[] = featuredServices.map((s) => ({
    id: s.id,
    label: s.titre,
    image: s.product_image ? s.product_image.split('|')[0] : null,
    featured: Boolean(s.is_featured),
  }));

  const orderedVisible = visibleDefs
    .map((def) => blocks.find((b) => b.key === def.key) || { key: def.key, enabled: def.defaultEnabled })
    .map((b) => HOME_BLOCKS.find((d) => d.key === b.key)! )
    .filter(Boolean);

  const orderedRows = (() => {
    const visibleKeys = visibleDefs.map((d) => d.key);
    const list: HomeBlockConfig[] = [];
    blocks.forEach((b) => {
      if (visibleKeys.includes(b.key)) list.push(b);
    });
    return list;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-layout-masonry-line mr-2 text-primary-500"></i>
          Blocs de la page d'accueil
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Choisissez les blocs à afficher sur votre page d'accueil et leur ordre d'apparition.
        </p>
      </div>

      {error && (
        <div className="mb-5 px-4 py-3 rounded-lg text-sm font-medium bg-red-50 text-red-600">{error}</div>
      )}

      {/* Blocs */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5">
            <i className="ri-stack-line text-primary-500"></i>
            Blocs &amp; ordre
          </h3>
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Ordre par défaut
          </button>
        </div>

        <p className="text-xs text-foreground-400 mb-4 flex items-center gap-1">
          <i className="ri-drag-move-2-line"></i>
          Glissez une ligne pour changer l'ordre. Activez ou masquez un bloc avec l'interrupteur.
        </p>

        {orderedRows.length === 0 ? (
          <p className="text-sm text-foreground-400 text-center py-8">Aucun bloc disponible.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedRows.map((b) => b.key)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {orderedRows.map((b) => {
                  const def = orderedVisible.find((d) => d.key === b.key) || HOME_BLOCKS.find((d) => d.key === b.key)!;
                  return (
                    <SortableBlockRow
                      key={b.key}
                      id={b.key}
                      icon={def.icon}
                      label={blockLabel(def, caps.restaurant)}
                      description={def.description}
                      enabled={b.enabled}
                      onToggle={(val) => toggleBlock(b.key, val)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="flex items-center gap-3 mt-5 pt-4 border-t border-background-200/70">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enregistrement...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Enregistrer l'affichage
              </>
            )}
          </button>
          {saved && (
            <span className="text-sm text-accent-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>
              Enregistré !
            </span>
          )}
        </div>
      </div>

      {/* Mise en page de la grille « Nos services » */}
      {caps.services && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5 mb-1">
            <i className="ri-layout-grid-line text-primary-500"></i>
            Mise en page — Nos services
          </h3>
          <p className="text-xs text-foreground-500 mb-4">
            Choisissez le nombre de colonnes de la grille « Nos services » sur votre page d'accueil.
          </p>

          <div className="grid grid-cols-3 gap-3">
            {SERVICE_COLUMN_OPTIONS.map((n) => {
              const active = serviceColumns === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setServiceColumns(n)}
                  className={`flex flex-col items-center gap-2.5 px-3 py-4 rounded-lg border transition-colors cursor-pointer ${
                    active
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-background-200/70 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <span className={n === 2 ? 'grid grid-cols-2 gap-0.5' : n === 4 ? 'grid grid-cols-4 gap-0.5' : 'grid grid-cols-3 gap-0.5'}>
                    {Array.from({ length: n }).map((_, i) => (
                      <span
                        key={i}
                        className={`block w-2.5 h-4 rounded-sm ${active ? 'bg-primary-500' : 'bg-background-300/70'}`}
                      ></span>
                    ))}
                  </span>
                  <span className={`text-xs font-medium ${active ? 'text-primary-700' : 'text-foreground-600'}`}>
                    {n} colonnes
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleSaveColumns}
              disabled={savingColumns}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {savingColumns ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Enregistrer la mise en page
                </>
              )}
            </button>
            {columnsSaved && (
              <span className="text-sm text-accent-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>
                Enregistré !
              </span>
            )}
          </div>
        </div>
      )}

      {/* Mise en page de la grille « Nos produits » */}
      {caps.products && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5 mb-1">
            <i className="ri-layout-grid-line text-primary-500"></i>
            Mise en page — {caps.restaurant ? 'Notre menu' : 'Nos produits'}
          </h3>
          <p className="text-xs text-foreground-500 mb-4">
            Choisissez le nombre de colonnes de la grille « {caps.restaurant ? 'Notre menu' : 'Nos produits'} » sur votre page d'accueil.
          </p>

          <div className="grid grid-cols-3 gap-3">
            {PRODUCT_COLUMN_OPTIONS.map((n) => {
              const active = productColumns === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setProductColumns(n)}
                  className={`flex flex-col items-center gap-2.5 px-3 py-4 rounded-lg border transition-colors cursor-pointer ${
                    active
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-background-200/70 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <span className={n === 2 ? 'grid grid-cols-2 gap-0.5' : n === 4 ? 'grid grid-cols-4 gap-0.5' : 'grid grid-cols-3 gap-0.5'}>
                    {Array.from({ length: n }).map((_, i) => (
                      <span
                        key={i}
                        className={`block w-2.5 h-4 rounded-sm ${active ? 'bg-primary-500' : 'bg-background-300/70'}`}
                      ></span>
                    ))}
                  </span>
                  <span className={`text-xs font-medium ${active ? 'text-primary-700' : 'text-foreground-600'}`}>
                    {n} colonnes
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleSaveProductColumns}
              disabled={savingProductColumns}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {savingProductColumns ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Enregistrer la mise en page
                </>
              )}
            </button>
            {productColumnsSaved && (
              <span className="text-sm text-accent-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>
                Enregistré !
              </span>
            )}
          </div>
        </div>
      )}

      {/* Blocs « À propos / présentation » */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5 mb-1">
          <i className="ri-file-text-line text-primary-500"></i>
          Bloc « À propos / présentation »
        </h3>
        <p className="text-xs text-foreground-500 mb-4">
          Rédigez un texte de présentation affiché dans le bloc « À propos » de votre page d'accueil.
        </p>

        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre (facultatif)</label>
        <input
          type="text"
          value={aboutTitle}
          onChange={(e) => setAboutTitle(e.target.value)}
          maxLength={80}
          placeholder="À propos de nous"
          className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 mb-4"
        />

        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Texte de présentation</label>
        <textarea
          value={aboutContent}
          onChange={(e) => setAboutContent(e.target.value.slice(0, 800))}
          rows={5}
          maxLength={800}
          placeholder="Présentez votre activité, votre savoir-faire, vos valeurs..."
          className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 resize-none"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1 mb-4">{aboutContent.length}/800 caractères</p>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveAbout}
            disabled={savingAbout}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {savingAbout ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enregistrement...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Enregistrer la présentation
              </>
            )}
          </button>
          {aboutSaved && (
            <span className="text-sm text-accent-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>
              Enregistré !
            </span>
          )}
        </div>
      </div>

      {/* Bloc « Appel à l'action » */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5 mb-1">
          <i className="ri-megaphone-line text-accent-500"></i>
          Bloc « Appel à l'action »
        </h3>
        <p className="text-xs text-foreground-500 mb-4">
          Une bannière sur votre page d'accueil avec un bouton : demande d'avis, formulaire de contact, réservation, lien externe…
          Activez le bloc « Appel à l'action » dans la liste ci-dessus pour l'afficher.
        </p>

        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre</label>
        <input
          type="text"
          value={ctaTitle}
          onChange={(e) => setCtaTitle(e.target.value)}
          maxLength={80}
          placeholder="Ex : Une question ? Parlons-en"
          className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 mb-4"
        />

        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Texte</label>
        <textarea
          value={ctaText}
          onChange={(e) => setCtaText(e.target.value.slice(0, 300))}
          rows={3}
          maxLength={300}
          placeholder="Expliquez en une phrase ce que vous proposez au visiteur…"
          className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 resize-none mb-1"
        ></textarea>
        <p className="text-xs text-foreground-400 mb-4">{ctaText.length}/300 caractères</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Libellé du bouton</label>
            <input
              type="text"
              value={ctaButtonLabel}
              onChange={(e) => setCtaButtonLabel(e.target.value)}
              maxLength={40}
              placeholder="Ex : Nous contacter"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Destination du bouton</label>
            <input
              type="text"
              value={ctaUrl}
              onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="/contact ou https://…"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveCta}
            disabled={savingCta}
            className="flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {savingCta ? (
              <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
            ) : (
              <><i className="ri-check-line"></i>Enregistrer l'appel à l'action</>
            )}
          </button>
          {ctaSaved && (
            <span className="text-sm text-accent-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>
              Enregistré !
            </span>
          )}
        </div>
      </div>

      {/* Vedette : produits / services */}
      {((caps.products && !caps.restaurant) || caps.services) && (
        <div className={`grid grid-cols-1 gap-6 mb-6 ${caps.products && caps.services ? 'lg:grid-cols-2' : ''}`}>
          {caps.products && !caps.restaurant && (
            <FeaturedList
              title="Produits en vedette"
              icon="ri-shopping-bag-3-line"
              hint="Cliquez sur l'étoile pour afficher un produit dans le bloc « Produits en vedette » de votre site."
              emptyLabel="Aucun produit. Ajoutez-en depuis le dashboard Produits."
              items={productItems}
              onToggle={handleToggleProductFeatured}
            />
          )}
          {caps.services && (
            <FeaturedList
              title="Services en vedette"
              icon="ri-service-line"
              hint="Cliquez sur l'étoile pour afficher un service dans le bloc « Services en vedette » de votre site."
              emptyLabel="Aucun service. Ajoutez-en depuis le dashboard Services."
              items={serviceItems}
              onToggle={handleToggleServiceFeatured}
            />
          )}
        </div>
      )}

      {/* Calendrier de réservation par défaut */}
      {caps.services && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5 mb-1">
            <i className="ri-calendar-check-line text-accent-500"></i>
            Calendrier de réservation par défaut
          </h3>
          <p className="text-xs text-foreground-500 mb-4">
            Le bouton « Réserver » de votre site ouvrira directement ce calendrier (créé dans ZCalendar).
          </p>

          {bookingServices.length === 0 ? (
            <div className="text-sm text-foreground-400 flex items-center gap-2 py-3">
              <i className="ri-information-line"></i>
              Créez d'abord un service dans l'app ZCalendar pour pouvoir le sélectionner.
            </div>
          ) : (
            <>
              <select
                value={defaultServiceId ?? ''}
                onChange={(e) => setDefaultServiceId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">— Aucun (bouton Réserver générique) —</option>
                {bookingServices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.service_name}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleSaveDefault}
                  disabled={savingDefault}
                  className="flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {savingDefault ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Définir comme calendrier par défaut
                    </>
                  )}
                </button>
                {defaultSaved && (
                  <span className="text-sm text-accent-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i>
                    Enregistré !
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}